import { aiArchitect } from './pollinations';
import { MiniAppBlueprint, AiNetworkLog } from './types';
import { db } from '../api/db';
import { toast } from '../utils/toast';

export class AiStudioUI {
  private container: HTMLElement | null = null;
  private isGenerating: boolean = false;
  private isTestingPing: boolean = false;
  private onDeployCallback: ((blueprint: MiniAppBlueprint) => void) | null = null;
  private mobileTab: 'chat' | 'preview' = 'chat';
  private logsModalOpen: boolean = false;
  private logsUnsubscribe: (() => void) | null = null;

  init(container: HTMLElement, onDeploy?: (blueprint: MiniAppBlueprint) => void) {
    this.container = container;
    this.onDeployCallback = onDeploy || null;

    if (this.logsUnsubscribe) {
      this.logsUnsubscribe();
    }
    this.logsUnsubscribe = aiArchitect.onLogsChange(() => {
      this.updateLogBadge();
      if (this.logsModalOpen) {
        this.renderLogsList();
      }
    });

    this.render();
  }

  private updateLogBadge() {
    const badge = this.container?.querySelector('#ai-logs-badge');
    if (badge) {
      const logs = aiArchitect.getLogs();
      const failed = logs.filter(l => l.status === 'failed' || l.status === 'retrying').length;
      badge.textContent = `Logs (${logs.length}${failed > 0 ? ` • ${failed} failover` : ''})`;
    }
  }

  render() {
    if (!this.container) return;
    const blueprint = aiArchitect.getCurrentBlueprint();
    const history = aiArchitect.getHistory();
    const logs = aiArchitect.getLogs();
    const latestLog = logs[0];
    const isErrorState = latestLog && (latestLog.status === 'failed' || latestLog.status === 'retrying');

    this.container.innerHTML = `
      <div class="fixed inset-0 z-[1000] bg-slate-950 text-slate-100 flex flex-col md:flex-row overflow-hidden font-sans select-none animate-in fade-in duration-300 w-full max-w-full">
        
        <!-- MOBILE VIEW TOGGLE BAR (Visible on screens < 768px) -->
        <div class="md:hidden flex items-center justify-between p-2 bg-slate-900 border-b border-slate-800 shrink-0">
          <div class="flex bg-slate-950 p-1 rounded-xl w-full gap-1">
            <button id="ai-mobile-tab-chat" class="flex-1 py-2 text-center rounded-lg text-[9px] font-black uppercase tracking-wider transition-all ${this.mobileTab === 'chat' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-400 hover:text-white'}">
              💬 Architect Chat
            </button>
            <button id="ai-mobile-tab-preview" class="flex-1 py-2 text-center rounded-lg text-[9px] font-black uppercase tracking-wider transition-all ${this.mobileTab === 'preview' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-400 hover:text-white'}">
              📱 Live App Preview
            </button>
          </div>
        </div>

        <!-- LEFT / MAIN: AI ARCHITECT CHAT CONSOLE -->
        <div class="${this.mobileTab === 'chat' ? 'flex' : 'hidden md:flex'} flex-1 flex-col h-full bg-slate-900/95 md:border-r border-slate-800 backdrop-blur-xl relative z-10 overflow-hidden w-full">
          
          <!-- Top Bar -->
          <header class="p-3 sm:p-4 border-b border-slate-800 flex items-center justify-between bg-slate-950/60 shrink-0 gap-2">
            <div class="flex items-center gap-2.5 sm:gap-3 min-w-0">
              <div class="w-9 h-9 sm:w-10 sm:h-10 rounded-2xl bg-gradient-to-tr from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center text-white shadow-lg shadow-indigo-500/20 shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/></svg>
              </div>
              <div class="min-w-0">
                <div class="flex items-center gap-2 flex-wrap">
                  <h2 class="text-xs sm:text-sm font-black italic uppercase tracking-wider text-white truncate">AI Mini-App Architect</h2>
                  <span class="px-2 py-0.5 rounded-full text-[7px] sm:text-[8px] font-black uppercase tracking-widest bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">Pollinations Browser AI</span>
                </div>
                <p class="text-[8px] sm:text-[9px] text-slate-400 font-mono uppercase tracking-widest truncate">100% Client-Side • Auto Failover & Diagnostics</p>
              </div>
            </div>

            <!-- Header Controls: Diagnostics & Logs -->
            <div class="flex items-center gap-1.5 sm:gap-2 shrink-0">
              <button id="ai-logs-toggle-btn" class="px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-xl text-[8px] sm:text-[9px] font-black uppercase tracking-widest transition-all flex items-center gap-1.5 ${isErrorState ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40 hover:bg-amber-500/30' : 'bg-slate-800 hover:bg-slate-700 text-indigo-300 border border-slate-700'}">
                <span class="w-2 h-2 rounded-full ${isErrorState ? 'bg-amber-400 animate-pulse' : 'bg-emerald-400'}"></span>
                <span id="ai-logs-badge">Logs (${logs.length})</span>
              </button>

              <button id="ai-reset-btn" class="px-2.5 sm:px-3 py-1.5 sm:py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-[8px] sm:text-[9px] font-black uppercase tracking-widest transition-all">
                Reset
              </button>
              <button id="ai-close-btn" class="w-8 h-8 sm:w-9 sm:h-9 bg-slate-800 hover:bg-slate-700 rounded-xl flex items-center justify-center text-slate-400 hover:text-white transition-all">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
              </button>
            </div>
          </header>

          <!-- Preset Prompt Pills -->
          <div class="px-3 sm:px-4 py-2 bg-slate-950/40 border-b border-slate-800/80 flex items-center gap-2 overflow-x-auto scrolling-touch no-scrollbar shrink-0">
            <span class="text-[8px] font-black uppercase text-slate-500 tracking-wider whitespace-nowrap">Ideas:</span>
            <button class="ai-prompt-chip whitespace-nowrap px-3 py-1 bg-indigo-500/10 hover:bg-indigo-500/20 border border-indigo-500/30 rounded-xl text-[8px] sm:text-[9px] font-bold text-indigo-300 transition-all">
              ⚡ Cyberpunk TON Tap-to-Earn
            </button>
            <button class="ai-prompt-chip whitespace-nowrap px-3 py-1 bg-purple-500/10 hover:bg-purple-500/20 border border-purple-500/30 rounded-xl text-[8px] sm:text-[9px] font-bold text-purple-300 transition-all">
              💎 USDT Quest & Affiliate Hub
            </button>
            <button class="ai-prompt-chip whitespace-nowrap px-3 py-1 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 rounded-xl text-[8px] sm:text-[9px] font-bold text-amber-300 transition-all">
              🔥 Daily Streak & Spin Miner
            </button>
            <button class="ai-prompt-chip whitespace-nowrap px-3 py-1 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 rounded-xl text-[8px] sm:text-[9px] font-bold text-emerald-300 transition-all">
              🪙 Meme Coin Airdrop Portal
            </button>
          </div>

          <!-- Chat Stream History -->
          <div id="ai-chat-messages" class="flex-1 overflow-y-auto p-3 sm:p-4 space-y-4 text-xs font-sans leading-relaxed scroll-fix">
            ${history.length === 0 ? `
              <div class="py-8 sm:py-12 px-4 text-center max-w-md mx-auto space-y-4">
                <div class="w-14 h-14 sm:w-16 sm:h-16 rounded-3xl bg-indigo-500/10 border border-indigo-500/20 mx-auto flex items-center justify-center text-indigo-400">
                  <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 8V4H8"/><rect width="16" height="12" x="4" y="8" rx="2"/><path d="M2 14h2"/><path d="M20 14h2"/><path d="M15 13v2"/><path d="M9 13v2"/></svg>
                </div>
                <div class="space-y-1">
                  <h3 class="text-sm sm:text-base font-black italic uppercase text-white">Describe Your Earning App Idea</h3>
                  <p class="text-xs text-slate-400">Tell me what kind of Telegram Mini App you want to create (e.g. rewards, viral tasks, welcome bonuses, colors, currency).</p>
                </div>
                <div class="p-3.5 bg-slate-800/60 rounded-2xl border border-slate-700/50 text-[10px] text-slate-300 text-left space-y-1 font-mono">
                  <p class="text-indigo-400 font-bold uppercase tracking-wider">💡 Try prompting:</p>
                  <p>"Create a futuristic TON rewards mini-app called CyberTON with 500 coin signup bonus, 5 viral tasks, deep neon purple theme, and crypto withdrawals."</p>
                </div>
              </div>
            ` : history.map(msg => `
              <div class="flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'} animate-in fade-in duration-200">
                <div class="flex items-center gap-2 mb-1 px-1">
                  <span class="text-[8px] font-black uppercase tracking-widest ${msg.role === 'user' ? 'text-indigo-400' : 'text-purple-400'}">
                    ${msg.role === 'user' ? 'You' : 'Architect AI'}
                  </span>
                  ${msg.source ? `
                    <span class="text-[7px] font-mono px-1.5 py-0.2 rounded bg-slate-800/80 text-slate-400 border border-slate-700/50">
                      ${msg.source}
                    </span>
                  ` : ''}
                </div>
                <div class="max-w-[92%] sm:max-w-[85%] p-3.5 sm:p-4 rounded-2xl ${msg.role === 'user' ? 'bg-indigo-600 text-white rounded-tr-sm shadow-md' : 'bg-slate-800 border border-slate-700 text-slate-200 rounded-tl-sm shadow-md space-y-2'}">
                  <div class="whitespace-pre-wrap leading-relaxed break-words">${this.formatMarkdown(msg.content)}</div>
                </div>
              </div>
            `).join('')}

            ${this.isGenerating ? `
              <div class="flex flex-col items-start animate-in fade-in duration-200">
                <div class="flex items-center gap-2 mb-1 px-1">
                  <span class="text-[8px] font-black uppercase tracking-widest text-purple-400">Architect AI</span>
                </div>
                <div class="max-w-[85%] p-3.5 sm:p-4 rounded-2xl bg-slate-800 border border-slate-700 text-slate-200 rounded-tl-sm shadow-md flex items-center gap-3">
                  <div class="w-4 h-4 border-2 border-purple-400 border-t-transparent rounded-full animate-spin"></div>
                  <span class="text-xs text-purple-300 font-mono animate-pulse">Synthesizing blueprint with Pollinations AI...</span>
                </div>
              </div>
            ` : ''}
          </div>

          <!-- Bottom Action Bar & Input -->
          <div class="p-3 sm:p-4 bg-slate-950/80 border-t border-slate-800 space-y-2.5 shrink-0">
            <!-- Active Blueprint Quick Status Pill -->
            <div class="flex items-center justify-between px-3 py-2 bg-slate-900 rounded-xl border border-slate-800 text-[8px] sm:text-[9px] font-mono flex-wrap gap-1">
              <div class="flex items-center gap-2 sm:gap-3 flex-wrap">
                <span class="text-slate-400">APP: <b class="text-white">${blueprint.name}</b></span>
                <span class="text-slate-400">CURRENCY: <b class="text-indigo-400">${blueprint.currency_name}</b></span>
                <span class="text-slate-400">BONUS: <b class="text-emerald-400">+${blueprint.welcome_bonus}</b></span>
              </div>
              <span class="text-purple-400 font-bold uppercase tracking-wider">${blueprint.template_id}</span>
            </div>

            <!-- Chat Input Form -->
            <form id="ai-chat-form" class="flex gap-2">
              <input 
                id="ai-user-input" 
                type="text" 
                placeholder="Ask for changes (e.g. 'make it darker', 'add 4 tasks', 'set bonus to 250')..." 
                class="flex-1 bg-slate-900 border border-slate-700 focus:border-indigo-500 rounded-2xl px-3.5 sm:px-4 py-2.5 sm:py-3 text-xs text-white placeholder-slate-500 outline-none transition-all font-medium"
                ${this.isGenerating ? 'disabled' : ''}
              />
              <button 
                type="submit" 
                class="px-4 sm:px-5 py-2.5 sm:py-3 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white rounded-2xl text-xs font-black uppercase tracking-wider flex items-center gap-1.5 sm:gap-2 shadow-lg shadow-indigo-500/20 active:scale-95 transition-all disabled:opacity-50 shrink-0"
                ${this.isGenerating ? 'disabled' : ''}
              >
                <span>SEND</span>
                <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
              </button>
            </form>
          </div>
        </div>

        <!-- RIGHT: LIVE SANDBOX MOCKUP & AUTO-HOST ACTIONS -->
        <div class="${this.mobileTab === 'preview' ? 'flex' : 'hidden md:flex'} w-full md:w-[420px] lg:w-[460px] bg-slate-950 flex-col h-full border-t md:border-t-0 md:border-l border-slate-800 shrink-0 overflow-hidden">
          
          <!-- Sandbox Header -->
          <div class="p-3 sm:p-4 border-b border-slate-800 flex items-center justify-between bg-slate-900/60 shrink-0">
            <div>
              <p class="text-[8px] sm:text-[9px] font-black uppercase tracking-widest text-slate-400">Live Device Sandbox</p>
              <h3 class="text-xs font-black italic uppercase text-white">Interactive Mini-App Preview</h3>
            </div>
            <div class="flex items-center gap-1.5">
              <span class="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
              <span class="text-[8px] font-mono uppercase text-emerald-400 font-bold">Realtime</span>
            </div>
          </div>

          <!-- Sandbox Mobile Screen Viewport -->
          <div class="flex-1 overflow-y-auto p-3 sm:p-4 flex items-center justify-center bg-slate-950/80 scroll-fix">
            
            <!-- Phone Frame -->
            <div class="w-full max-w-[340px] h-[540px] sm:h-[580px] rounded-[2.5rem] border-[6px] border-slate-800 bg-slate-900 shadow-2xl overflow-hidden flex flex-col relative" style="background-color: ${blueprint.theme.bgColor}; font-family: ${blueprint.theme.fontFamily}, sans-serif;">
              
              <!-- Telegram Dynamic Header Pill -->
              <div class="h-6 w-full bg-slate-950/80 flex items-center justify-between px-6 shrink-0">
                <span class="text-[8px] text-slate-400 font-mono">9:41</span>
                <div class="w-16 h-3 bg-slate-800 rounded-full"></div>
                <span class="text-[8px] text-slate-400 font-mono">100%</span>
              </div>

              <!-- Mini App Viewport Inside Phone -->
              <div class="flex-1 overflow-y-auto p-4 space-y-4 text-white scroll-fix" style="color: #ffffff;">
                
                <!-- App Top Identity -->
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2">
                    <div class="w-8 h-8 rounded-xl flex items-center justify-center shadow-lg font-black text-sm" style="background: linear-gradient(135deg, ${blueprint.theme.primaryColor}, ${blueprint.theme.secondaryColor});">
                      ⚡
                    </div>
                    <div>
                      <h4 class="text-xs font-black uppercase tracking-tight">${blueprint.name}</h4>
                      <p class="text-[7px] uppercase font-mono tracking-widest opacity-60">Telegram Mini-App</p>
                    </div>
                  </div>
                  <div class="px-2 py-0.5 rounded-lg text-[8px] font-black uppercase tracking-wider" style="background-color: ${blueprint.theme.primaryColor}22; color: ${blueprint.theme.primaryColor}; border: 1px solid ${blueprint.theme.primaryColor}44;">
                    LIVE
                  </div>
                </div>

                <!-- Balance / Staking Hero Card -->
                <div class="p-5 rounded-2xl relative overflow-hidden border border-white/10 shadow-xl text-center space-y-2" style="background: linear-gradient(145deg, ${blueprint.theme.primaryColor}33, ${blueprint.theme.secondaryColor}22); border-color: ${blueprint.theme.primaryColor}44;">
                  <div class="absolute top-0 left-0 right-0 h-1" style="background: linear-gradient(90deg, ${blueprint.theme.primaryColor}, ${blueprint.theme.secondaryColor});"></div>
                  <p class="text-[8px] font-black uppercase tracking-[0.2em] opacity-70">Total Available Balance</p>
                  <h2 class="text-3xl font-black italic tracking-tighter text-white">
                    ${(blueprint.welcome_bonus || 100).toLocaleString()} <span class="text-xs font-bold not-italic" style="color: ${blueprint.theme.accentColor};">${blueprint.currency_name}</span>
                  </h2>
                  <div class="grid grid-cols-2 gap-2 pt-3 mt-3 border-t border-white/10 text-[8px]">
                    <div class="bg-black/30 p-2 rounded-xl">
                      <span class="opacity-60 block uppercase font-bold">Invite Reward</span>
                      <b class="text-white font-mono">+${blueprint.referral_reward} ${blueprint.currency_name}</b>
                    </div>
                    <div class="bg-black/30 p-2 rounded-xl">
                      <span class="opacity-60 block uppercase font-bold">Min Payout</span>
                      <b class="text-white font-mono">${blueprint.min_withdrawal} ${blueprint.currency_name}</b>
                    </div>
                  </div>
                </div>

                <!-- Missions / Tasks Section -->
                <div class="space-y-2">
                  <div class="flex justify-between items-center px-1">
                    <span class="text-[8px] font-black uppercase tracking-widest opacity-60">Featured Missions</span>
                    <span class="text-[7px] font-mono opacity-50">${blueprint.initial_tasks?.length || 0} active</span>
                  </div>

                  <div class="space-y-1.5">
                    ${(blueprint.initial_tasks || []).map(t => `
                      <div class="p-2.5 rounded-xl bg-black/40 border border-white/5 flex items-center justify-between gap-2">
                        <div class="flex items-center gap-2 min-w-0">
                          <div class="w-6 h-6 rounded-lg bg-white/10 flex items-center justify-center text-xs shrink-0">
                            ${t.icon || '⚡'}
                          </div>
                          <div class="min-w-0">
                            <p class="text-[9px] font-black leading-tight text-white truncate">${t.title}</p>
                            <span class="text-[8px] font-bold font-mono" style="color: ${blueprint.theme.accentColor};">+${t.reward} ${blueprint.currency_name}</span>
                          </div>
                        </div>
                        <button class="px-2.5 py-1 rounded-lg text-[7px] font-black uppercase tracking-wider shrink-0" style="background-color: ${blueprint.theme.primaryColor}; color: #000;">
                          Start
                        </button>
                      </div>
                    `).join('')}
                  </div>
                </div>
              </div>

              <!-- Telegram Bottom Bar Mockup -->
              <div class="h-12 w-full bg-slate-950/90 border-t border-white/5 flex items-center justify-around px-2 shrink-0">
                ${['Home', 'Earn', 'Invite', 'Wallet'].map((tabName, idx) => `
                  <div class="flex flex-col items-center gap-0.5 ${idx === 0 ? 'text-white' : 'opacity-40'}">
                    <span class="text-xs">${idx === 0 ? '🏠' : idx === 1 ? '🎯' : idx === 2 ? '🤝' : '🏦'}</span>
                    <span class="text-[6px] font-black uppercase tracking-widest">${tabName}</span>
                  </div>
                `).join('')}
              </div>
            </div>
          </div>

          <!-- Bottom Deployment Control Bar -->
          <div class="p-3 sm:p-4 bg-slate-900 border-t border-slate-800 space-y-2 shrink-0">
            <button id="ai-autohost-btn" class="w-full py-3.5 sm:py-4 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 shadow-xl shadow-emerald-500/20 active:scale-95 transition-all">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/><path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/></svg>
              <span>AUTO-HOST & LAUNCH APP</span>
            </button>
            
            <div class="grid grid-cols-2 gap-2">
              <button id="ai-export-json-btn" class="py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-[8px] font-black uppercase tracking-widest transition-all">
                Export JSON
              </button>
              <button id="ai-open-factory-btn" class="py-2.5 bg-slate-800 hover:bg-slate-700 text-indigo-300 rounded-xl text-[8px] font-black uppercase tracking-widest transition-all">
                Manual Edit
              </button>
            </div>
          </div>
        </div>
      </div>
    `;

    this.bindEvents();
    this.scrollToBottom();
  }

  private bindEvents() {
    if (!this.container) return;

    // Mobile tabs
    const chatTabBtn = this.container.querySelector('#ai-mobile-tab-chat');
    const previewTabBtn = this.container.querySelector('#ai-mobile-tab-preview');
    if (chatTabBtn) {
      chatTabBtn.addEventListener('click', () => {
        this.mobileTab = 'chat';
        this.render();
      });
    }
    if (previewTabBtn) {
      previewTabBtn.addEventListener('click', () => {
        this.mobileTab = 'preview';
        this.render();
      });
    }

    // Diagnostics & Logs Toggle
    const logsBtn = this.container.querySelector('#ai-logs-toggle-btn');
    if (logsBtn) {
      logsBtn.addEventListener('click', () => {
        this.showDiagnosticsModal();
      });
    }

    // Close button
    const closeBtn = this.container.querySelector('#ai-close-btn');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        if (this.logsUnsubscribe) {
          this.logsUnsubscribe();
        }
        this.container?.remove();
      });
    }

    // Reset button
    const resetBtn = this.container.querySelector('#ai-reset-btn');
    if (resetBtn) {
      resetBtn.addEventListener('click', () => {
        if (confirm('Start a new mini-app idea conversation?')) {
          aiArchitect.resetContext();
          this.render();
        }
      });
    }

    // Prompt Chips
    const chips = this.container.querySelectorAll('.ai-prompt-chip');
    chips.forEach(chip => {
      chip.addEventListener('click', () => {
        const text = chip.textContent?.trim() || '';
        this.submitPrompt(`Create a ${text} mini-app with high rewards, custom missions, and modern responsive layout.`);
      });
    });

    // Chat Form Submit
    const form = this.container.querySelector('#ai-chat-form') as HTMLFormElement;
    const input = this.container.querySelector('#ai-user-input') as HTMLInputElement;
    if (form && input) {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        const query = input.value.trim();
        if (query) {
          this.submitPrompt(query);
          input.value = '';
        }
      });
    }

    // Auto-Host Button
    const hostBtn = this.container.querySelector('#ai-autohost-btn');
    if (hostBtn) {
      hostBtn.addEventListener('click', () => this.handleAutoHost());
    }

    // Export JSON Button
    const exportBtn = this.container.querySelector('#ai-export-json-btn');
    if (exportBtn) {
      exportBtn.addEventListener('click', () => {
        const blueprint = aiArchitect.getCurrentBlueprint();
        const jsonStr = JSON.stringify(blueprint, null, 2);
        navigator.clipboard.writeText(jsonStr);
        toast.show('Complete App Blueprint copied to clipboard!', 'success');
      });
    }

    // Manual Edit Button
    const editBtn = this.container.querySelector('#ai-open-factory-btn');
    if (editBtn) {
      editBtn.addEventListener('click', () => {
        const bp = aiArchitect.getCurrentBlueprint();
        if (this.onDeployCallback) {
          this.onDeployCallback(bp);
        }
        this.container?.remove();
      });
    }
  }

  private showDiagnosticsModal() {
    this.logsModalOpen = true;
    const existing = document.getElementById('ai-diagnostics-modal');
    if (existing) existing.remove();

    const config = aiArchitect.getConfig();
    const modal = document.createElement('div');
    modal.id = 'ai-diagnostics-modal';
    modal.className = 'fixed inset-0 z-[2500] flex items-center justify-center p-3 sm:p-6 bg-slate-950/90 backdrop-blur-xl animate-in fade-in duration-200';
    
    modal.innerHTML = `
      <div class="bg-slate-900 border border-slate-700/80 rounded-3xl max-w-2xl w-full max-h-[90vh] flex flex-col shadow-2xl text-white overflow-hidden">
        
        <!-- Modal Top Bar -->
        <div class="p-4 sm:p-5 border-b border-slate-800 flex items-center justify-between bg-slate-950/60 shrink-0">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-2xl bg-indigo-500/10 border border-indigo-500/30 flex items-center justify-center text-indigo-400 font-black">
              ⚡
            </div>
            <div>
              <h3 class="text-sm font-black italic uppercase tracking-wider text-white">AI Diagnostics & Network Logs</h3>
              <p class="text-[9px] text-slate-400 font-mono">Real-time inspection of browser Pollinations requests & failovers</p>
            </div>
          </div>

          <button id="close-diagnostics-btn" class="w-8 h-8 rounded-xl bg-slate-800 hover:bg-slate-700 flex items-center justify-center text-slate-400 hover:text-white transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
          </button>
        </div>

        <!-- Connection Test & Why it didn't respond banner -->
        <div class="p-4 bg-slate-950/50 border-b border-slate-800 space-y-3 shrink-0">
          
          <div class="flex items-center justify-between gap-2 flex-wrap">
            <div class="flex items-center gap-2">
              <span class="text-[9px] font-black uppercase tracking-wider text-slate-400">Endpoint Health:</span>
              <span id="ping-status-badge" class="px-2 py-0.5 rounded-lg text-[8px] font-mono font-bold bg-slate-800 text-slate-300">
                Ready to Ping
              </span>
            </div>
            
            <div class="flex items-center gap-2">
              <button id="ping-test-btn" class="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-[8px] font-black uppercase tracking-wider transition-all flex items-center gap-1.5">
                <span class="w-1.5 h-1.5 rounded-full bg-white"></span>
                <span>Test Cluster Latency</span>
              </button>
              <button id="clear-logs-btn" class="px-2.5 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-[8px] font-black uppercase tracking-wider transition-all">
                Clear Logs
              </button>
              <button id="copy-diagnostics-btn" class="px-2.5 py-1.5 bg-slate-800 hover:bg-slate-700 text-indigo-300 rounded-xl text-[8px] font-black uppercase tracking-wider transition-all">
                Copy Report
              </button>
            </div>
          </div>

          <!-- Limitations & Mitigation Reference Box -->
          <div class="p-3 bg-slate-800/60 rounded-2xl border border-slate-700/50 text-[9px] text-slate-300 space-y-1.5">
            <div class="flex items-center gap-1.5 text-indigo-400 font-bold uppercase tracking-wider">
              <span>🔍 Common Reasons Free Pollinations Might Fail & How We Mitigate:</span>
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1 text-[8px] leading-relaxed text-slate-300">
              <div class="p-2 bg-slate-900/60 rounded-xl border border-slate-700/40 space-y-0.5">
                <b class="text-amber-300 block">1. HTTP 429 (Queue Full for Free IP)</b>
                <span>Public IP queue concurrency. <i>Fix:</i> We automatically cascade from POST to GET and fallback to universal load-balancing.</span>
              </div>
              <div class="p-2 bg-slate-900/60 rounded-xl border border-slate-700/40 space-y-0.5">
                <b class="text-blue-300 block">2. AdBlockers & CORS Restrictions</b>
                <span>Extensions blocking cross-origin fetch. <i>Fix:</i> Our client neural heuristic generator synthesizes your exact requested changes offline.</span>
              </div>
              <div class="p-2 bg-slate-900/60 rounded-xl border border-slate-700/40 space-y-0.5">
                <b class="text-emerald-300 block">3. JSON Schema Truncation</b>
                <span>Free LLMs omitting closing brackets. <i>Fix:</i> Our lenient parser fixes trailing commas, repair braces, and validates keys.</span>
              </div>
              <div class="p-2 bg-slate-900/60 rounded-xl border border-slate-700/40 space-y-0.5">
                <b class="text-purple-300 block">4. Optional Custom API Token</b>
                <span>Have an <code>enter.pollinations.ai</code> key? Enter it below to bypass free public IP queues completely.</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Model & Optional Token Configuration Row -->
        <div class="p-3 bg-slate-950/30 border-b border-slate-800 flex flex-col sm:flex-row items-stretch sm:items-center gap-2 shrink-0">
          <div class="flex items-center gap-2">
            <span class="text-[8px] font-mono text-slate-400 whitespace-nowrap uppercase">Model Mode:</span>
            <select 
              id="pollinations-model-select"
              class="bg-slate-900 border border-slate-700 rounded-xl px-2.5 py-1 text-[9px] text-white outline-none focus:border-indigo-500 font-mono"
            >
              <option value="" ${!config.preferredModel ? 'selected' : ''}>Auto / Default Free Cluster (Recommended)</option>
              <option value="mistral" ${config.preferredModel === 'mistral' ? 'selected' : ''}>Mistral (Open)</option>
              <option value="deepseek" ${config.preferredModel === 'deepseek' ? 'selected' : ''}>DeepSeek (Open)</option>
              <option value="qwen" ${config.preferredModel === 'qwen' ? 'selected' : ''}>Qwen (Open)</option>
              <option value="openai" ${config.preferredModel === 'openai' ? 'selected' : ''}>OpenAI (API Key)</option>
            </select>
          </div>
          
          <div class="flex items-center gap-2 flex-1">
            <input 
              id="pollinations-api-key-input" 
              type="password" 
              placeholder="Optional enter.pollinations.ai API Key" 
              value="${config.apiKey || ''}"
              class="flex-1 bg-slate-900 border border-slate-700 rounded-xl px-3 py-1 text-[9px] text-white outline-none focus:border-indigo-500 font-mono"
            />
            <button id="save-key-btn" class="px-3 py-1 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-[8px] font-bold uppercase transition-all whitespace-nowrap">
              Save Config
            </button>
          </div>
        </div>

        <!-- Log List Stream Container -->
        <div id="diagnostics-logs-container" class="flex-1 overflow-y-auto p-4 space-y-2.5 scroll-fix bg-slate-950/60 font-mono text-[9px]">
          <!-- Populated by renderLogsList() -->
        </div>
      </div>
    `;

    document.body.appendChild(modal);
    this.renderLogsList();

    // Close Modal
    modal.querySelector('#close-diagnostics-btn')?.addEventListener('click', () => {
      this.logsModalOpen = false;
      modal.remove();
    });

    // Ping Test
    const pingBtn = modal.querySelector('#ping-test-btn') as HTMLButtonElement;
    const pingBadge = modal.querySelector('#ping-status-badge') as HTMLElement;
    if (pingBtn) {
      pingBtn.addEventListener('click', async () => {
        if (this.isTestingPing) return;
        this.isTestingPing = true;
        pingBadge.className = 'px-2 py-0.5 rounded-lg text-[8px] font-mono font-bold bg-amber-500/20 text-amber-300 border border-amber-500/40 animate-pulse';
        pingBadge.textContent = 'Pinging...';

        const res = await aiArchitect.testConnection();
        this.isTestingPing = false;

        if (res.ok) {
          pingBadge.className = 'px-2 py-0.5 rounded-lg text-[8px] font-mono font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/40';
          pingBadge.textContent = `🟢 Healthy (${res.latencyMs}ms)`;
        } else {
          pingBadge.className = 'px-2 py-0.5 rounded-lg text-[8px] font-mono font-bold bg-rose-500/20 text-rose-300 border border-rose-500/40';
          pingBadge.textContent = `🔴 ${res.error || 'Failed'} (${res.latencyMs}ms)`;
        }
      });
    }

    // Clear Logs
    modal.querySelector('#clear-logs-btn')?.addEventListener('click', () => {
      aiArchitect.clearLogs();
      this.renderLogsList();
    });

    // Copy Diagnostics
    modal.querySelector('#copy-diagnostics-btn')?.addEventListener('click', () => {
      const logs = aiArchitect.getLogs();
      const report = JSON.stringify(logs, null, 2);
      navigator.clipboard.writeText(report);
      toast.show('Diagnostics JSON report copied to clipboard!', 'success');
    });

    // Save Config
    modal.querySelector('#save-key-btn')?.addEventListener('click', () => {
      const keyInput = modal.querySelector('#pollinations-api-key-input') as HTMLInputElement;
      const modelSelect = modal.querySelector('#pollinations-model-select') as HTMLSelectElement;
      aiArchitect.saveConfig({ 
        apiKey: keyInput.value.trim(),
        preferredModel: modelSelect ? modelSelect.value : ''
      });
      toast.show('Pollinations settings saved & updated!', 'success');
    });
  }

  private renderLogsList() {
    const listContainer = document.getElementById('diagnostics-logs-container');
    if (!listContainer) return;

    const logs = aiArchitect.getLogs();
    if (logs.length === 0) {
      listContainer.innerHTML = `
        <div class="py-12 text-center text-slate-500 space-y-2">
          <p class="text-sm">No network logs yet.</p>
          <p class="text-[8px]">Type a prompt in the chat to see real-time network telemetry and diagnostics.</p>
        </div>
      `;
      return;
    }

    listContainer.innerHTML = logs.map(log => {
      const timeStr = new Date(log.timestamp).toLocaleTimeString();
      let statusClass = 'bg-slate-800 text-slate-400';
      let statusDot = 'bg-slate-400';

      if (log.status === 'success') {
        statusClass = 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20';
        statusDot = 'bg-emerald-400';
      } else if (log.status === 'failed') {
        statusClass = 'bg-rose-500/10 text-rose-400 border border-rose-500/20';
        statusDot = 'bg-rose-400';
      } else if (log.status === 'retrying') {
        statusClass = 'bg-amber-500/10 text-amber-400 border border-amber-500/20';
        statusDot = 'bg-amber-400 animate-pulse';
      } else if (log.status === 'pending') {
        statusClass = 'bg-indigo-500/10 text-indigo-400 border border-indigo-500/20';
        statusDot = 'bg-indigo-400 animate-pulse';
      }

      return `
        <div class="p-3 bg-slate-900/90 rounded-2xl border border-slate-800 space-y-1.5 animate-in fade-in duration-200">
          <div class="flex items-center justify-between gap-2 flex-wrap">
            <div class="flex items-center gap-2">
              <span class="w-1.5 h-1.5 rounded-full ${statusDot}"></span>
              <span class="font-bold text-white uppercase">${log.stage}</span>
              <span class="px-1.5 py-0.2 rounded text-[7px] font-black uppercase ${statusClass}">
                ${log.httpStatus ? `HTTP ${log.httpStatus}` : log.status}
              </span>
            </div>
            <div class="flex items-center gap-2 text-slate-500 text-[8px]">
              ${log.durationMs !== undefined ? `<span>${log.durationMs}ms</span>` : ''}
              <span>${timeStr}</span>
            </div>
          </div>

          <div class="text-slate-400 break-all space-y-0.5">
            <p><span class="text-slate-500">Method & URL:</span> <b class="text-indigo-300 font-bold">${log.method}</b> <span class="text-slate-300">${log.endpoint}</span></p>
            ${log.payloadSummary ? `<p><span class="text-slate-500">Payload:</span> <span class="text-slate-300">${log.payloadSummary}</span></p>` : ''}
            ${log.detailedReason ? `
              <div class="p-2 bg-amber-500/10 border border-amber-500/20 rounded-xl text-amber-300 text-[8px] my-1 leading-normal">
                <b>Reason:</b> ${log.detailedReason}
              </div>
            ` : ''}
            ${log.responseSnippet ? `
              <div class="p-2 bg-slate-950 rounded-xl border border-slate-800 text-slate-400 text-[8px] truncate">
                <span class="text-slate-600">Response Preview:</span> ${log.responseSnippet}
              </div>
            ` : ''}
          </div>
        </div>
      `;
    }).join('');
  }

  private async submitPrompt(promptText: string) {
    if (this.isGenerating) return;
    this.isGenerating = true;
    this.render();

    try {
      const res = await aiArchitect.generate(promptText);
      if (res.sourceUsed.includes('Client Neural')) {
        toast.show('⚡ Synthesized blueprint update', 'info');
      } else {
        toast.show('✨ Pollinations AI blueprint updated', 'success');
      }
    } catch (e: any) {
      toast.show(`AI notice: ${e.message}`, 'info');
    } finally {
      this.isGenerating = false;
      this.render();
      setTimeout(() => this.scrollToBottom(), 50);
    }
  }

  private async handleAutoHost() {
    const blueprint = aiArchitect.getCurrentBlueprint();
    toast.show(`Initiating auto-hosting for ${blueprint.name}...`, 'info');

    try {
      const user = (window as any).nexus?.user || { id: '12345678' };
      const alias = blueprint.name.toLowerCase().replace(/[^a-z0-9]/g, '_').substring(0, 18) + '_' + Math.random().toString(36).substring(2, 6);

      const appData: any = {
        name: blueprint.name,
        currency_name: blueprint.currency_name,
        template_id: blueprint.template_id || 'hybrid_rewards_x',
        icon: blueprint.icon || 'zap',
        alias: alias,
        owner_id: user.id.toString(),
        welcome_bonus: blueprint.welcome_bonus || 100,
        referral_reward: blueprint.referral_reward || 50,
        conversion_enabled: blueprint.conversion_enabled ?? true,
        conversion_rate: blueprint.conversion_rate || 0.001,
        conversion_currency: blueprint.conversion_currency || 'USDT',
        min_withdrawal: blueprint.min_withdrawal || 500,
        mandatory_tasks_to_withdraw: blueprint.mandatory_tasks_to_withdraw || 0,
        loading_config: blueprint.loading_config || {
          text: 'Synchronizing Neural Link...',
          color: blueprint.theme.primaryColor || '#6366f1'
        },
        theme: blueprint.theme,
        tabs_config: blueprint.tabs_config || [
          { id: 'home', enabled: true, label: 'Home' },
          { id: 'tasks', enabled: true, label: 'Tasks' },
          { id: 'spin', enabled: true, label: 'Spin' },
          { id: 'refer', enabled: true, label: 'Recruits' },
          { id: 'wallet', enabled: true, label: 'Wallet' },
          { id: 'profile', enabled: true, label: 'Profile' }
        ],
        bot_config: blueprint.bot_config || {}
      };

      // 1. Create App in Firestore
      const newApp = await db.createApp(appData);
      const appId = newApp.id;

      // 2. Populate Initial Tasks in Firestore
      if (blueprint.initial_tasks && blueprint.initial_tasks.length > 0) {
        for (const task of blueprint.initial_tasks) {
          await db.createTask({
            ...task,
            app_id: appId
          }).catch((err) => console.warn('Failed to seed initial task:', err));
        }
      }

      // 3. Show Success & Auto-Host Clearance Modal
      const hostedUrl = `${window.location.origin}/?alias=${alias}`;
      const directRoute = `${window.location.origin}/app/${alias}`;

      this.showHostSuccessModal(blueprint.name, alias, hostedUrl, directRoute);
      toast.show('🎉 Mini-App successfully hosted & live on network!', 'success');

    } catch (e: any) {
      toast.show(`Deployment error: ${e.message}`, 'error');
    }
  }

  private showHostSuccessModal(appName: string, alias: string, url: string, directRoute: string) {
    const modal = document.createElement('div');
    modal.className = 'fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-slate-950/90 backdrop-blur-xl animate-in fade-in duration-300';
    modal.innerHTML = `
      <div class="bg-slate-900 border border-slate-700/80 rounded-3xl p-6 sm:p-8 max-w-lg w-full text-center space-y-6 shadow-2xl text-white">
        <div class="w-16 h-16 bg-emerald-500/10 border border-emerald-500/30 rounded-2xl flex items-center justify-center text-emerald-400 mx-auto">
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
        </div>

        <div class="space-y-2">
          <span class="text-[9px] font-black uppercase tracking-[0.3em] text-emerald-400">Auto-Hosting Active</span>
          <h2 class="text-2xl font-black italic uppercase tracking-tight text-white">${appName} is Live!</h2>
          <p class="text-xs text-slate-400">Your AI-generated Telegram Mini-App has been provisioned, tasks configured, and routed.</p>
        </div>

        <!-- Live Link Box -->
        <div class="p-4 bg-slate-950 border border-slate-800 rounded-2xl space-y-2 text-left">
          <span class="text-[8px] font-mono uppercase text-slate-500 tracking-wider">Live WebApp URL:</span>
          <div class="flex items-center justify-between gap-2">
            <span class="text-xs font-mono text-indigo-400 break-all select-all font-bold">${url}</span>
          </div>
        </div>

        <!-- BotFather Guidance Pill -->
        <div class="p-3 bg-indigo-500/10 border border-indigo-500/20 rounded-xl text-left text-[9px] text-slate-300 space-y-1 font-mono">
          <p class="text-indigo-400 font-bold uppercase">🤖 Telegram BotFather Setup:</p>
          <p>1. Open @BotFather on Telegram ➔ /newapp</p>
          <p>2. Paste WebApp URL: <code>${url}</code></p>
        </div>

        <!-- Action Buttons -->
        <div class="space-y-2">
          <button id="copy-live-link" class="w-full py-4 bg-indigo-600 hover:bg-indigo-500 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-lg shadow-indigo-600/30 active:scale-95 transition-all">
            COPY SHAREABLE LINK
          </button>
          <div class="grid grid-cols-2 gap-2">
            <button id="open-app-live" class="py-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-[9px] font-black uppercase tracking-widest transition-all">
              Open App Now
            </button>
            <button id="return-dashboard" class="py-3 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all">
              Platform Console
            </button>
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    const copyBtn = modal.querySelector('#copy-live-link') as HTMLButtonElement;
    if (copyBtn) {
      copyBtn.addEventListener('click', () => {
        navigator.clipboard.writeText(url);
        copyBtn.innerText = 'COPIED TO CLIPBOARD!';
        setTimeout(() => { copyBtn.innerText = 'COPY SHAREABLE LINK'; }, 2000);
      });
    }

    const openBtn = modal.querySelector('#open-app-live');
    if (openBtn) {
      openBtn.addEventListener('click', () => {
        window.location.href = `/?alias=${alias}`;
      });
    }

    const dashBtn = modal.querySelector('#return-dashboard');
    if (dashBtn) {
      dashBtn.addEventListener('click', () => {
        modal.remove();
        this.container?.remove();
        if ((window as any).nexus?.handleBackToDashboard) {
          (window as any).nexus.handleBackToDashboard();
        }
      });
    }
  }

  private scrollToBottom() {
    const el = this.container?.querySelector('#ai-chat-messages');
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }

  private formatMarkdown(text: string): string {
    return text
      .replace(/### (.*?)\n/g, '<h4 class="text-xs font-bold uppercase tracking-wider text-indigo-400 mt-2 mb-1">$1</h4>')
      .replace(/## (.*?)\n/g, '<h3 class="text-sm font-black uppercase text-white mt-2 mb-1">$1</h3>')
      .replace(/---/g, '<hr class="border-slate-700 my-2" />')
      .replace(/\*\*(.*?)\*\*/g, '<b class="text-white font-bold">$1</b>')
      .replace(/\*(.*?)\*/g, '<i class="text-slate-300">$1</i>')
      .replace(/`([^`]+)`/g, '<code class="bg-slate-950 px-1.5 py-0.5 rounded text-indigo-300 font-mono text-[10px] border border-slate-800">$1</code>')
      .replace(/^\s*[\-\*]\s+(.*)$/gm, '<div class="flex items-start gap-1.5 ml-1 my-0.5"><span class="text-indigo-400 font-bold">•</span><span>$1</span></div>');
  }
}

export const aiStudio = new AiStudioUI();
